import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  Loader2,
  Building2,
  Truck,
  Store,
  Briefcase,
  CheckCircle2,
  Phone,
  FileText,
  CalendarClock,
  BadgeDollarSign,
  ShieldCheck,
} from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { Reveal } from "@/components/Reveal";
import { api, BUSINESS } from "@/lib/config";

const BUSINESS_TYPES = [
  { id: "dealership", label: "Dealership", icon: Store },
  { id: "composite", label: "Composite / Body Shop", icon: Building2 },
  { id: "llc", label: "Small LLC / Business", icon: Briefcase },
  { id: "fleet", label: "Fleet Operator", icon: Truck },
  { id: "other", label: "Other", icon: Building2 },
];

const PERKS = [
  { icon: BadgeDollarSign, title: "Volume pricing", body: "Per-vehicle contract rates that beat one-off detailing — the more vehicles, the better the number." },
  { icon: CalendarClock, title: "Recurring schedule", body: "Weekly, bi-weekly, or monthly lot service that keeps your inventory and vehicles camera-ready." },
  { icon: ShieldCheck, title: "On-site & insured", body: "We bring everything to your lot or shop. No vehicles leave your property, no downtime for your team." },
];

const inputCls =
  "w-full h-12 px-4 rounded-lg bg-black border border-zinc-800 text-white placeholder:text-zinc-600 focus:border-white focus:outline-none transition-colors";

export default function FleetPage() {
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    company_name: "",
    contact_name: "",
    email: "",
    phone: "",
    business_type: "dealership",
    fleet_size: "",
    service_interest: "",
    message: "",
  });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const valid =
    form.company_name.trim().length >= 2 &&
    form.contact_name.trim().length >= 2 &&
    /^\S+@\S+\.\S+$/.test(form.email) &&
    form.phone.trim().length >= 7 &&
    form.fleet_size.trim();

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (!payload.service_interest) delete payload.service_interest;
      if (!payload.message) delete payload.message;
      await api.post("/fleet-inquiries", payload);
      setDone(true);
      window.scrollTo(0, 0);
    } catch (e) {
      toast.error(e.response?.data?.detail ? (typeof e.response.data.detail === "string" ? e.response.data.detail : "Check your details.") : "Couldn't send. Try again or call us.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="bg-black min-h-screen text-zinc-200">
        <Nav />
        <section data-testid="fleet-success" className="pt-40 pb-28 px-6 md:px-10 max-w-2xl mx-auto text-center">
          <div className="mx-auto w-16 h-16 rounded-full bg-white text-black flex items-center justify-center mb-8">
            <CheckCircle2 size={32} />
          </div>
          <h1 className="font-heading text-5xl tracking-tighter font-light text-chrome mb-4">Inquiry received.</h1>
          <p className="text-zinc-400 leading-relaxed mb-10">
            Thanks {form.contact_name.split(" ")[0]} — we'll reach out to {form.company_name} shortly to talk contract pricing and scheduling.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <a href={BUSINESS.phoneHref} className="inline-flex items-center gap-2 h-12 px-7 rounded-full bg-white text-black font-medium hover:bg-zinc-200 transition-colors">
              <Phone size={14} /> Call {BUSINESS.phone}
            </a>
            <Link to="/" className="inline-flex items-center gap-2 h-12 px-7 rounded-full border border-zinc-700 text-zinc-300 hover:border-white hover:text-white transition-colors">
              Back to home
            </Link>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div className="bg-black min-h-screen text-zinc-200 overflow-x-hidden">
      <Nav />

      {/* HERO */}
      <section data-testid="fleet-page" className="relative pt-36 pb-20 px-6 md:px-10 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-zinc-900 via-black to-black" />
        <div className="relative max-w-5xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <Link to="/" className="inline-flex items-center gap-2 text-zinc-500 hover:text-white text-sm mb-10 transition-colors">
              <ArrowLeft size={14} /> Back
            </Link>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 bg-white/5 text-[11px] uppercase tracking-[0.25em] text-zinc-300 mb-8">
              <Truck size={12} /> Fleet & Commercial
            </div>
            <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl tracking-tighter font-light text-chrome mb-6">
              Contracts for<br />fleets & dealerships.
            </h1>
            <p className="text-zinc-400 text-lg max-w-2xl leading-relaxed">
              Keep every vehicle showroom-ready. We partner with dealerships, composite & body shops, and small businesses on recurring, per-vehicle detailing contracts — done on your lot.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href="#fleet-form" className="inline-flex items-center gap-2 h-13 py-3 px-7 rounded-full bg-white text-black font-medium hover:bg-zinc-200 transition-colors">
                Request a quote <ArrowRight size={16} />
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* PERKS */}
      <section className="py-16 border-y border-zinc-900">
        <div className="max-w-6xl mx-auto px-6 md:px-10 grid md:grid-cols-3 gap-6">
          {PERKS.map((p) => {
            const Icon = p.icon;
            return (
              <Reveal key={p.title}>
                <div className="rounded-2xl border border-zinc-900 bg-zinc-950 p-8 h-full">
                  <Icon className="text-zinc-200 mb-5" size={26} />
                  <h3 className="font-heading text-xl text-white mb-2">{p.title}</h3>
                  <p className="text-zinc-400 text-sm leading-relaxed">{p.body}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* FORM */}
      <section id="fleet-form" className="py-24 px-6 md:px-10">
        <div className="max-w-3xl mx-auto">
          <Reveal>
            <div className="text-xs uppercase tracking-[0.25em] text-zinc-500 mb-4">Request a quote</div>
            <h2 className="font-heading text-3xl md:text-4xl tracking-tighter font-light text-chrome mb-3">Let's talk numbers.</h2>
            <p className="text-zinc-400 mb-10">Tell us about your fleet and we'll come back with contract pricing.</p>
          </Reveal>

          <div className="space-y-6">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-3 block">Business type</label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {BUSINESS_TYPES.map((t) => {
                  const Icon = t.icon;
                  const active = form.business_type === t.id;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => update("business_type", t.id)}
                      data-testid={`fleet-type-${t.id}`}
                      className={`rounded-xl border p-4 text-left transition-colors ${active ? "border-white bg-zinc-950" : "border-zinc-900 hover:border-zinc-700"}`}
                    >
                      <Icon size={18} className="text-zinc-300 mb-2" />
                      <div className="text-white text-sm font-medium">{t.label}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2 block">Company name</label>
                <input value={form.company_name} onChange={(e) => update("company_name", e.target.value)} placeholder="ABC Motors" className={inputCls} data-testid="fleet-company" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2 block">Your name</label>
                <input value={form.contact_name} onChange={(e) => update("contact_name", e.target.value)} placeholder="Full name" className={inputCls} data-testid="fleet-contact" />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2 block">Email</label>
                <input type="email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="you@company.com" className={inputCls} data-testid="fleet-email" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2 block">Phone</label>
                <input value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="603-555-5555" className={inputCls} data-testid="fleet-phone" />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2 block">Fleet size</label>
                <input value={form.fleet_size} onChange={(e) => update("fleet_size", e.target.value)} placeholder="e.g. 10–25 vehicles" className={inputCls} data-testid="fleet-size" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2 block">Service interest (optional)</label>
                <input value={form.service_interest} onChange={(e) => update("service_interest", e.target.value)} placeholder="e.g. Weekly lot washing" className={inputCls} data-testid="fleet-interest" />
              </div>
            </div>

            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2 block">Anything else? (optional)</label>
              <textarea value={form.message} onChange={(e) => update("message", e.target.value)} rows={4} placeholder="Tell us about your needs, timeline, or vehicle types." className={`${inputCls} h-auto py-3 resize-none`} data-testid="fleet-message" />
            </div>

            <button
              type="button"
              onClick={submit}
              disabled={!valid || submitting}
              data-testid="fleet-submit"
              className="inline-flex items-center gap-2 h-14 px-9 rounded-full bg-white text-black font-medium hover:bg-zinc-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors shadow-[0_0_30px_rgba(255,255,255,0.12)]"
            >
              {submitting ? <><Loader2 size={16} className="animate-spin" /> Sending…</> : <>Request fleet quote <ArrowRight size={16} /></>}
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
